import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OverlayGrayBackground } from "./OverlayGrayBackground";
import { SelectDropdown } from "./SelectDropdown";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { LoaderSpinner } from "./LoaderSpinner";
import { X } from "lucide-react";
import axios from 'axios';
import { toast } from 'react-toastify';
import { TanStackTable } from "./TanStackTable";
import { TrashIcon, CubeIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { CurrencyDollarIcon } from '@heroicons/react/24/solid';
import { createDraftPurchaseOrder, PurchaseOrdersManagementLoadingKeys, getPurchaseOrderById, updatePurchaseOrder } from "../slices/managementSlices/purchaseOrdersSlice";
import { statuses, enumStatuses } from "../consts/status";
import ProductSelectorModal from "./ProductSelectorModal";
import NoChargeConfirmationModal, { resolveSelectedVendorName } from "./NoChargeConfirmationModal";
import { arrayHasItems, formatCurrencyUsdFixedTwo, formatCurrencyUsdTwoOrFour, getAllowedUserPermissions } from "../helpers/common";
import { ROUTES } from "../consts/general";
import { CHILDREN_TAB_ACCOUNTS_PAYABLE, isAdminRole } from "../consts/management/users/users";

const QUANTITY_COMMIT_DELAY = 275;

const PurchaseOrderQuantityCell = memo(({ productId, value, onCommit }) => {
  const [draftValue, setDraftValue] = useState(String(value ?? ''));
  const commitTimerRef = useRef(null);
  const isDirtyRef = useRef(false);
  const latestValueRef = useRef(String(value ?? ''));

  useEffect(() => {
    const nextValue = String(value ?? '');
    latestValueRef.current = nextValue;
    if (!isDirtyRef.current) {
      setDraftValue(nextValue);
    }
  }, [value]);

  useEffect(() => () => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
  }, []);

  const commit = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    isDirtyRef.current = false;
    const quantity = parseInt(latestValueRef.current, 10) || 0;
    onCommit(productId, quantity);
  }, [onCommit, productId]);

  const handleChange = useCallback((event) => {
    const nextValue = event.target.value;
    latestValueRef.current = nextValue;
    isDirtyRef.current = true;
    setDraftValue(nextValue);
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(commit, QUANTITY_COMMIT_DELAY);
  }, [commit]);

  return (
    <input
      type="text"
      inputMode="numeric"
      value={draftValue}
      onChange={handleChange}
      onBlur={commit}
      className="no-arrows-by-input w-16 border rounded px-1 py-0.5"
    />
  );
});

const PurchaseOrderSidebar = ({
  purchaseOrderId,
  onClose,
  onSuccessSubmit,
  variant,
  isOpen,
  embedded = false,
}) => {
  const { loadingKeys, loading, purchaseOrderDetails } = useSelector((state) => state.purchaseOrdersManagement);
  const currentUser = useSelector((state) => state.auth.currentUser);

  const { control, register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
        vendor: "",
        warehouse: ""
    },
  });

  const selectedVendor = watch("vendor");
  const { childIdToAccess } = getAllowedUserPermissions({
    permissions: currentUser?.permissions || [],
    isAdmin: isAdminRole(currentUser?.role),
  });
  const canEditStatus = childIdToAccess.get(CHILDREN_TAB_ACCOUNTS_PAYABLE) === 'edit';

  const dispatch = useDispatch();
  const [vendorsOptions, setVendorOptions] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [warehousesOptions, setWarehousesOptions] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [productsList, setProductsList] = useState([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [noChargePrompt, setNoChargePrompt] = useState(null);
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [shipping, setShipping] = useState(0);
  const [estimatedFreight, setEstimatedFreight] = useState('');
  const [freightSuggestion, setFreightSuggestion] = useState(null);
  const [freightEstimateTouched, setFreightEstimateTouched] = useState(false);
  const initialEstimatedFreightRef = useRef('');
  const estimatedFreightRef = useRef('');
  const freightEstimateTouchedRef = useRef(false);
  const [vendorInfo, setVendorInfo] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [materialSpecsMap, setMaterialSpecsMap] = useState({});
  const materialSpecsMapRef = useRef(materialSpecsMap);
  const materialFetchedIdsRef = useRef(new Set());
  const materialFetchesInFlightRef = useRef(new Set());
  const materialLoadKeyRef = useRef(null);

  /** Product id → ShipHero lineItemId from the first load of this edit session (restore when user removes and re-adds the same product). */
  const initialEditLineItemIdsByProductIdRef = useRef({});
  const hasSeededInitialLineItemIdsRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setNoChargePrompt(null);
      initialEditLineItemIdsByProductIdRef.current = {};
      hasSeededInitialLineItemIdsRef.current = false;
      materialLoadKeyRef.current = null;
      materialFetchedIdsRef.current.clear();
      materialFetchesInFlightRef.current.clear();
      return;
    }
    initialEditLineItemIdsByProductIdRef.current = {};
    hasSeededInitialLineItemIdsRef.current = false;
  }, [isOpen, purchaseOrderId]);

  useEffect(() => {
    if (!isOpen || variant !== 'edit' || !purchaseOrderDetails) return;
    if (hasSeededInitialLineItemIdsRef.current) return;
    const items = purchaseOrderDetails.po_line_items;
    if (!Array.isArray(items)) return;
    hasSeededInitialLineItemIdsRef.current = true;
    items.forEach((li) => {
      if (li.id && li.lineItemId) {
        initialEditLineItemIdsByProductIdRef.current[li.id] = li.lineItemId;
      }
    });
  }, [isOpen, variant, purchaseOrderDetails]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const subtotal = useMemo(
    () => productsList.reduce((sum, { quantity, itemPrice }) => Number(sum || 0) + Number(quantity || 0) * Number(itemPrice || 0), 0),
    [productsList]
  );

  const total = useMemo(() => Number(subtotal) + Number(shipping), [subtotal, shipping]);

  const depositFirstAmount = useMemo(() =>
    ((vendorInfo?.depositFirst || 0) / 100) * total,
  [vendorInfo, total]);

  const depositSecondAmount = useMemo(() =>
    ((vendorInfo?.depositSecond || 0) / 100) * total,
  [vendorInfo, total]);

  const balanceAmount = useMemo(() =>
    ((vendorInfo?.balance || 0) / 100) * total,
  [vendorInfo, total]);

  const creditAmount = useMemo(() =>
    ((vendorInfo?.credit || 0) / 100) * total,
  [vendorInfo, total]);

  const paymentBreakdownTotal = useMemo(() => depositFirstAmount + depositSecondAmount + balanceAmount + creditAmount, [depositFirstAmount, depositSecondAmount, balanceAmount, creditAmount]);

  materialSpecsMapRef.current = materialSpecsMap;

  const requestMaterialSpecs = useCallback((ids) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean))).filter((id) => (
      !materialFetchedIdsRef.current.has(id) &&
      !materialFetchesInFlightRef.current.has(id)
    ));
    if (!uniqueIds.length) return;

    uniqueIds.forEach((id) => materialFetchesInFlightRef.current.add(id));
    Promise.all(uniqueIds.map((id) =>
      axios
        .get(`/api/v1/materials/${id}`)
        .then(({ data }) => [id, data?.data || []])
        .catch(() => [id, []])
    )).then((results) => {
      results.forEach(([id]) => {
        materialFetchesInFlightRef.current.delete(id);
        materialFetchedIdsRef.current.add(id);
      });
      setMaterialSpecsMap((previous) => ({
        ...previous,
        ...Object.fromEntries(results),
      }));
    });
  }, []);

  const commitProductQuantity = useCallback((productId, quantity) => {
    setProductsList((previous) => {
      const product = previous.find((item) => item.id === productId);
      if (!product || Number(product.quantity || 0) === quantity) return previous;
      return previous.map((item) => item.id === productId ? { ...item, quantity } : item);
    });
  }, []);

  const deleteProduct = useCallback((productId) => {
    setProductsList((previous) => previous.filter((product) => product.id !== productId));
  }, []);

  useEffect(() => {
    if (variant === 'create') {
      setValue('warehouse', warehousesOptions.find(w => w.label && w.label === ('Primary'))?.value || (warehousesOptions[0]?.value || ""));
    }
  }, [variant, setValue, warehousesOptions]);


  useEffect(() => {
    setVendorsLoading(true);
    setWarehousesLoading(true);

    Promise.all([
      axios.get('/api/v1/management/vendors-options'),
      axios.get('/api/v1/management/warehouses')
    ])
    .then(([vendorsResponse, warehousesResponse]) => {
      setVendorOptions(vendorsResponse.data);
      const wh = warehousesResponse.data || [];
      setWarehousesOptions(wh);
      const primary = wh.find(w => w.label && w.label.toLowerCase().includes('primary')) || wh[0];
      if (primary) {
        setValue('warehouse', primary.value);
      }
    })
    .catch(() => {})
    .finally(() => {
      setVendorsLoading(false);
      setWarehousesLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !selectedVendor) return;
    setGroupsLoading(true);
    axios
      .get('/api/v1/vendor-products-grouped', { params: { vendorId: selectedVendor } })
      .then(({ data }) => setGroupedProducts(data || []))
      .catch(() => setGroupedProducts([]))
      .finally(() => setGroupsLoading(false));
  }, [isOpen, selectedVendor]);

  useEffect(() => {
    setProductsList([]);
    setShipping(0);
    setGroupedProducts([]);
  }, [selectedVendor]);

  useEffect(() => {
    if (!selectedVendor) {
      setVendorInfo(null);
      return;
    }
    axios
      .get('/api/v1/management/vendor', { params: { id: selectedVendor } })
      .then(({ data }) => setVendorInfo(data.vendor))
      .catch(() => setVendorInfo(null));
  }, [selectedVendor]);

  useEffect(() => {
    if (purchaseOrderId && variant === 'edit') {
        dispatch(getPurchaseOrderById({ id: purchaseOrderId }));
    }
  }, [purchaseOrderId, variant]);

  const defaultSort = { id: 'name', desc: false };

  const [sorting, setSorting] = useState([defaultSort]);
  useEffect(() => {
    if (sorting.length === 0) return;
    const [{ id: sortKey, desc }] = sorting;

    setProductsList(prev =>
      [...prev].sort((a, b) => {
        const getValue = (item) => {
          if (sortKey === 'totalPrice') {
            return (item.quantity || 0) * (item.itemPrice || 0);
          }
          return item[sortKey];
        };

        const aVal = getValue(a);
        const bVal = getValue(b);

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }

        return desc ? bVal - aVal : aVal - bVal;
      })
    );
  }, [sorting]);

  useEffect(() => {
    if (variant === 'edit' && purchaseOrderDetails) {
      reset({
        vendor: purchaseOrderDetails.vendor,
        status: purchaseOrderDetails.status,
        warehouse:
          warehousesOptions.find(w => w.label && w.label.toLowerCase().includes('primary'))?.value ||
          (warehousesOptions[0]?.value || "")
      });
      setShipping(Number(purchaseOrderDetails.shipping_cost));
      const existingEstimate = purchaseOrderDetails.estimated_freight == null
        ? ''
        : String(purchaseOrderDetails.estimated_freight);
      setEstimatedFreight(existingEstimate);
      estimatedFreightRef.current = existingEstimate;
      initialEstimatedFreightRef.current = existingEstimate;
      setFreightEstimateTouched(existingEstimate !== '');
      freightEstimateTouchedRef.current = existingEstimate !== '';
      setProductsList(purchaseOrderDetails.po_line_items);
      const materialLoadKey = purchaseOrderDetails.id || purchaseOrderId;
      if (materialLoadKeyRef.current !== materialLoadKey) {
        materialLoadKeyRef.current = materialLoadKey;
        setMaterialSpecsMap({});
        materialFetchedIdsRef.current.clear();
        materialFetchesInFlightRef.current.clear();
      }
    }
  }, [variant, purchaseOrderDetails, purchaseOrderId, reset, warehousesOptions]);

  useEffect(() => {
    if (!selectedVendor || !productsList.some((item) => item.id && Number(item.quantity) > 0)) {
      setFreightSuggestion(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post('/api/v1/po-ledger/freight-estimate/preview', {
          vendor_id: selectedVendor,
          line_items: productsList.map((item) => ({ product_id: item.id, quantity: item.quantity })),
          exclude_po_id: variant === 'edit' ? purchaseOrderId : undefined,
        });
        const suggestion = data?.data || null;
        setFreightSuggestion(suggestion);
        if (!freightEstimateTouchedRef.current) {
          const amount = suggestion?.amount == null ? '' : String(suggestion.amount);
          estimatedFreightRef.current = amount;
          setEstimatedFreight(amount);
        }
      } catch {
        setFreightSuggestion(null);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [selectedVendor, productsList, variant, purchaseOrderId]);

  const groupIdsKey = useMemo(() => (
    Array.from(new Set(productsList.map((product) => product.groupId).filter(Boolean)))
      .sort()
      .join(',')
  ), [productsList]);

  useEffect(() => {
    const groupIds = groupIdsKey ? groupIdsKey.split(',') : [];
    if (!groupIds.length) {
      setInstructions([]);
      return;
    }
    Promise.all(
      groupIds.map(id =>
        axios
          .get(`/api/v1/instructions/${id}`)
          .then(({ data }) => ({ groupId: id, instructions: data?.data || [] }))
      )
    )
      .then(results => {
        setInstructions(results);
      })
      .catch(() => setInstructions([]));
  }, [groupIdsKey]);

  useEffect(() => {
    requestMaterialSpecs(productsList.map((product) => product.id));
  }, [productsList, requestMaterialSpecs]);

  const columns = useMemo(() => [
    {
      header: "Product",
      accessorKey: "name",
      enableSorting: true,
      cell: info => {
        const { name, sku, image, id, lineItemId } = info.row.original;
        const materials = materialSpecsMapRef.current[id] || [];
        return (
          <div className="flex">
            {image ? (
              <img
                src={image}
                alt={name}
                className="w-10 h-10 object-contain mr-2 rounded"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/40x40/e6e6e6/cccccc?text=+';
                }}
              />
            ) : (
              <CubeIcon className="w-10 h-10 text-gray-300 mr-2 w-full max-w-[40px]" />
            )}
            <div className="flex flex-col">
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900 truncate max-w-[180px]">{name}</span>
                  <span className="text-sm text-gray-500">SKU: {sku}</span>
                </div>
                {variant === 'edit' && !lineItemId && (
                  <div className="flex items-center justify-center">
                    <ExclamationTriangleIcon 
                      className="w-5 h-5 text-red-500 cursor-pointer" 
                    />
                  </div>
                )}
              </div>
              {materials.length > 0 && (
                <div className="bg-gray-100 p-1 mt-1 rounded">
                  <div className="text-xs font-semibold">Material Specifications</div>
                  {materials.map((m, idx) => (
                    <div key={idx} className="text-xs text-black whitespace-pre-line">
                      {m.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      header: "Quantity",
      accessorKey: "quantity",
      enableSorting: true,
      cell: ({ row, getValue }) => {
        return (
          <PurchaseOrderQuantityCell
            productId={row.original.id}
            value={getValue()}
            onCommit={commitProductQuantity}
          />
      );
      }
    },
    {
      header: "Unit cost",
      accessorKey: "itemPrice",
      enableSorting: true,
      cell: ({ getValue }) => formatCurrencyUsdTwoOrFour(getValue() || 0)
    },
    {
      header: "Total cost",
      accessorKey: "totalPrice",
      enableSorting: true,
      cell: info => {
        const { quantity, itemPrice } = info.row.original;
        const lineTotal = Number(quantity || 0) * Number(itemPrice || 0);
        return formatCurrencyUsdFixedTwo(lineTotal);
      }
    },
    {
      header: "Actions",
      cell: ({ row }) => {
          return (
              <div className={`flex justify-center ${row.index % 2 === 0 ? '!bg-white' : '!bg-gray-50'}`}>
                  <button
                      className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      onClick={(e) => {
                          e.stopPropagation();
                          deleteProduct(row.original.id);
                      }}
                  >
                       <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
              </div>
          )
      }
  }
  ], [commitProductQuantity, deleteProduct, variant]);

  const handlePurchaseOrderSubmit = async (dataForPO, isEdit) => {
    let shouldToast = false;
    let failureMessage;
    try {
      if (isEdit) {
        if (purchaseOrderDetails?.status === 'DRAFT') {
          shouldToast = true;
          await axios.put(`/api/v1/management/purchase-order/${purchaseOrderId}/draft`, dataForPO);
        } else {
          await dispatch(updatePurchaseOrder({ poData: dataForPO, id: purchaseOrderId })).unwrap();
        }
        if (estimatedFreight !== initialEstimatedFreightRef.current && estimatedFreight !== '') {
          shouldToast = true;
          failureMessage = 'Unable to save freight estimate';
          await axios.post(`/api/v1/po-ledger/${purchaseOrderId}/freight-estimate`, {
            amount: estimatedFreight,
            source: 'USER',
          });
          shouldToast = false;
        }
      } else {
        await dispatch(createDraftPurchaseOrder({ poData: dataForPO })).unwrap();
      }
      setNoChargePrompt(null);
      onSuccessSubmit?.();
    } catch (error) {
      const responseData = error?.response?.data || error;
      if (responseData?.code === 'zero_cost_confirmation_required') {
        setNoChargePrompt({
          dataForPO,
          isEdit,
          lines: responseData.lines || [],
        });
        return;
      }
      if (shouldToast) {
        toast.error(
          failureMessage || responseData?.error || error.message || 'Unable to save purchase order',
          { position: 'bottom-right' },
        );
      }
    }
  };

  const onSubmit = async (data) => {
    const lineItemsWithNotes = productsList.map(item => ({
      ...item,
      note: (materialSpecsMap[item.id] || []).map(m => m.description).join('\n')
    }));

    const vendorNote = [
      ...instructions.flatMap(g => g.instructions.map(i => `${i.description}\n`)),
      vendorInfo?.defaultNote
    ].filter(Boolean).join('\n');

    if (variant === 'edit') {
      const dataForPO = {
        ...data,
        po_line_items: lineItemsWithNotes,
        notes: vendorNote,
        total_value: total,
        shipping_cost: shipping,
        estimated_freight: estimatedFreight === '' ? null : estimatedFreight,
        estimated_freight_basis: freightSuggestion?.amount != null
          && Math.abs(Number(estimatedFreight) - Number(freightSuggestion.amount)) < 0.005
          ? freightSuggestion
          : null,
        warehouse_id: data.warehouse,
        customer_account_id: 'QWNjb3VudDo2NjYxOA==' // Child account ID as specified by ShipHero support
      };
      await handlePurchaseOrderSubmit(dataForPO, true);
      return;
    }
 
    if (variant === 'create') {
      const dataForPO = {
        ...data,
        po_line_items: lineItemsWithNotes,
        notes: vendorNote,
        total_value: total,
        eden_status: "ACTIVE",
        shipping_cost: shipping,
        estimated_freight: estimatedFreight === '' ? null : estimatedFreight,
        estimated_freight_basis: freightSuggestion?.amount != null
          && Math.abs(Number(estimatedFreight) - Number(freightSuggestion.amount)) < 0.005
          ? freightSuggestion
          : null,
        warehouse_id: data.warehouse,
        customer_account_id: 'QWNjb3VudDo2NjYxOA==' // Child account ID as specified by ShipHero support
      };
      await handlePurchaseOrderSubmit(dataForPO, false);
      return;
    }
  };

  const confirmNoCharge = async ({ reason }) => {
    if (!noChargePrompt) return;
    const { dataForPO, isEdit, lines } = noChargePrompt;
    await handlePurchaseOrderSubmit({
      ...dataForPO,
      no_charge_confirmation: {
        reason,
        lines: lines.map(({ sku, quantity }) => ({ sku, quantity })),
      },
    }, isEdit);
  };

  const title = variant === 'edit' ? 'Edit Purchase Order' : 'Create purchase order';

  const formId = "purchase-order-form";

  const statusOptions = statuses.map((status) => ({
    label: enumStatuses[status],
    value: status
  }));

  const POIsLoading = () => loading && loadingKeys.includes(PurchaseOrdersManagementLoadingKeys.getPurchaseOrderById);
  const POIsCreateLoading = () => loading && loadingKeys.includes(PurchaseOrdersManagementLoadingKeys.createPurchaseOrder);
  const POIsUpdateLoading = () => loading && loadingKeys.includes(PurchaseOrdersManagementLoadingKeys.updatePurchaseOrder);

  const initialLoading = vendorsLoading || warehousesLoading;

  if (!isOpen) return null;

  const withNewSkus = arrayHasItems(productsList) && variant === 'edit' && productsList.some((item) => !item.lineItemId);

  const containerClass = embedded
    ? 'w-full h-full bg-white flex flex-col'
    : 'fixed right-0 top-0 h-full w-[880px] max-w-full max-[639px]:top-14 max-[639px]:h-[calc(100dvh-3.5rem)] bg-white border-l shadow-lg z-50 flex flex-col';

  return (
    <div className={containerClass}>
      <div className="flex items-start justify-between p-6 border-b">
        <h1 className="text-xl font-bold">{title}</h1>
        <button className="h-8 w-8" onClick={onClose} type="button">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className={`relative flex-1 ${POIsCreateLoading() || POIsUpdateLoading() || POIsLoading() ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        {!initialLoading && (POIsCreateLoading() || POIsUpdateLoading() || POIsLoading()) && (
          <>
            <OverlayGrayBackground additionalClass="!fixed"/>
          </>
        )}
        <div className="p-6 h-full">
          {initialLoading ? (
            <div className="z-10 flex h-full items-center justify-center">
              <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' />
            </div>
          ) : (
            <>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" id={formId}>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Supplier Information</h3>
                <div>
                  <label htmlFor="vendor" className="block text-sm font-medium text-gray-900">Vendor</label>
                  <Controller
                    id="vendor"
                    name="vendor"
                    control={control}
                    defaultValue={null}
                    rules={{ required: 'Vendor is required' }}
                    render={({ field }) => (
                      variant === 'edit' ? (
                        <input
                          type="text"
                          disabled
                          value={resolveSelectedVendorName({
                            variant,
                            selectedVendor: field.value,
                            vendorsOptions,
                            purchaseOrderDetails,
                            fallback: '',
                          })}
                          className="mt-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                        />
                      ) : (
                        <div className="relative mt-1">
                          {vendorsLoading && (
                            <div className="absolute inset-0 flex items-center justify-center z-10">
                              <LoaderSpinner width="25" height="25" border="3" colorSpinner="black" />
                            </div>
                          )}
                          <SelectDropdown
                            options={vendorsOptions}
                            value={field.value}
                            onChange={field.onChange}
                            menuSearchable
                            portal
                            searchPlaceholder="Search vendors..."
                          />
                        </div>
                      )
                    )}
                  />
                  {errors.vendor && (
                    <p className="mt-1 text-red-500 text-base">{errors.vendor.message}</p>
                  )}
                </div>

                <input type="hidden" {...register('warehouse', { required: true })} />

                {variant === "edit" && selectedVendor ? (
                  <div className="mt-3">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-900">Status</label>
                    <Controller
                      id="status"
                      name="status"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <SelectDropdown
                          options={statusOptions}
                          value={field.value}
                          onChange={field.onChange}
                          className="mt-1"
                          disabled={!canEditStatus}
                          tooltip={!canEditStatus ? "Finance (Accounts Payable) permission required" : undefined}
                        />
                      )}
                    />
                  </div>
                ) : null}
              </div>
            </form>
            {selectedVendor && (
              <>
                <div className="my-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Products</h3>
                    {productsList.length > 0 && (
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 relative"
                        onClick={() => setIsProductModalOpen(true)}
                      >
                        {groupsLoading && (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <LoaderSpinner width="18" height="18" border="2" colorSpinner="black" />
                          </span>
                        )}
                        <span className={groupsLoading ? 'opacity-0' : ''}>Update Products</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-700">Add products to your purchase order.</p>
                </div>

                {productsList.length === 0 ? (
                  <div className="my-4 flex flex-col items-center justify-center border-2 border-dashed rounded-md py-10">
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 relative"
                      onClick={() => setIsProductModalOpen(true)}
                    >
                      {groupsLoading && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <LoaderSpinner width="18" height="18" border="2" colorSpinner="black" />
                        </span>
                      )}
                      <span className={groupsLoading ? 'opacity-0' : ''}>Add Products</span>
                    </button>
                  </div>
                ) : (
                  <TanStackTable
                    data={productsList}
                    overrideStickyOptions={{
                      top: 80,
                    }}
                    columns={columns}
                    additionalData={{
                      sorting,
                      setSorting
                    }}
                    enableSorting={true}
                  />
                )}
                <div className="mt-6 space-y-3">
                  <h3 className="text-lg font-medium text-gray-900">Order Summary</h3>
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Subtotal</label>
                    <span>{formatCurrencyUsdFixedTwo(subtotal)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Shipping</label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-1" />
                      <input
                        type="text"
                        inputMode="decimal"
                        value={shipping}
                        onChange={e => setShipping(parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                        className="no-arrows-by-input w-32 text-right border border-gray-300 rounded px-2 py-1"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Total</label>
                    <span>{formatCurrencyUsdFixedTwo(total)}</span>
                  </div>
                </div>

                {vendorInfo && (
                  <div className="mt-6 space-y-1">
                    <h3 className="text-lg font-medium text-gray-900">Payment Breakdown</h3>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Deposit 1 ({vendorInfo.depositFirst || 0}%)</span>
                      <span>{formatCurrencyUsdFixedTwo(depositFirstAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Deposit 2 ({vendorInfo.depositSecond || 0}%)</span>
                      <span>{formatCurrencyUsdFixedTwo(depositSecondAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Balance ({vendorInfo.balance || 0}%)</span>
                      <span>{formatCurrencyUsdFixedTwo(balanceAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Credit ({vendorInfo.credit || 0}%)</span>
                      <span>{formatCurrencyUsdFixedTwo(creditAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-gray-200 pt-1">
                      <span className="text-sm font-medium text-gray-700">Total</span>
                      <span>{formatCurrencyUsdFixedTwo(paymentBreakdownTotal)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-b border-yellow-600 py-2 mt-2">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Estimated Freight</span>
                        <span className="ml-2 rounded-full border border-yellow-600 px-2 py-0.5 text-xs">ESTIMATE</span>
                        <span className="ml-2 text-xs text-gray-500">not included in PO Total</span>
                      </div>
                      <div className="flex items-center">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-1" />
                        <input
                          type="text"
                          inputMode="decimal"
                          value={estimatedFreight}
                          onChange={(event) => {
                            freightEstimateTouchedRef.current = true;
                            estimatedFreightRef.current = event.target.value;
                            setFreightEstimateTouched(true);
                            setEstimatedFreight(event.target.value);
                          }}
                          placeholder="0.00"
                          className="no-arrows-by-input w-32 text-right border border-gray-300 rounded px-2 py-1"
                        />
                      </div>
                    </div>
                    {freightSuggestion && (
                      <div className="text-xs text-gray-500">
                        {freightSuggestion.message || `Based on ${freightSuggestion.sources?.map((source) => `PO ${source.po_number || source.po_id} (${Math.round(source.weight * 100)}%)`).join(' and ')}, ${freightSuggestion.method}`}
                        {freightSuggestion.amount != null && freightEstimateTouched && (
                          <button
                            type="button"
                            className="ml-2 text-blue-600 underline"
                            onClick={() => {
                              const amount = String(freightSuggestion.amount);
                              estimatedFreightRef.current = amount;
                              freightEstimateTouchedRef.current = false;
                              setEstimatedFreight(amount);
                              setFreightEstimateTouched(false);
                            }}
                          >
                            Use suggestion
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {(instructions.length > 0 || (vendorInfo && vendorInfo.defaultNote)) && (
                  <div className="mt-6 space-y-2">
                    <h3 className="text-lg font-medium text-gray-900">Additional Notes</h3>
                    {instructions.map((group, idx) => (
                      <div key={idx} className="text-sm text-gray-700 whitespace-pre-line">
                        {groupedProducts.find(g => g.id === group.groupId)?.name && (
                          <p className="font-semibold">
                            {groupedProducts.find(g => g.id === group.groupId)?.name}
                          </p>
                        )}
                        {group.instructions.map((ins, i) => (
                          <div key={i} className="ml-2">
                            <p className="font-semibol">{ins.description}</p>
                          </div>
                        ))}
                      </div>
                    ))}
                    {vendorInfo?.defaultNote && (
                      <p className="text-sm text-gray-700 whitespace-pre-line">{vendorInfo.defaultNote}</p>
                    )}
                  </div>
                )}
              </>
            )}
            </>
          )}
        </div>
      </div>
      <div className="border-t px-6 py-4 bg-white flex flex-col gap-2">
        {variant === 'edit' && withNewSkus && (
          <div className="flex flex-row justify-between items-center w-full gap-2">
            <div className="text-sm text-primary">
              <span>This purchase order includes products with new SKUs. New products can only be added through ShipHero. </span>
              <a href={`${ROUTES.NOTES}#important-limitation-adding-products-to-existing-purchase-orders`} className="underline cursor-pointer" target="_blank">See the details link for more information.</a>
            </div>
          </div>
        )}
        <div className="flex flex-row justify-between items-center w-full gap-2">
          <button
            form={formId}
            type="submit"
            disabled={POIsCreateLoading() || POIsUpdateLoading() || POIsLoading() || withNewSkus}
            className="flex-1 whitespace-nowrap bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {variant === 'edit' ? 'Save Changes' : 'Create PO'}
          </button>
          <button
            type="button"
            className="flex-1 inline-flex justify-center whitespace-nowrap rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
      <ProductSelectorModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        selectedIds={productsList.map(p => p.id)}
        groups={groupedProducts}
        loading={groupsLoading}
        onConfirm={(items) => {
          const vendorName = vendorsOptions.find(v => v.value === selectedVendor)?.label || '';
          setProductsList(prev => {
            const map = {};
            prev.forEach(p => { map[p.id] = p; });
            items.forEach(it => {
              if (!map[it.id]) {
                const restoredLineItemId = initialEditLineItemIdsByProductIdRef.current[it.id];
                map[it.id] = {
                  ...it,
                  quantity: 1,
                  itemPrice: Number(it.unitCost || 0),
                  vendorName,
                  ...(restoredLineItemId ? { lineItemId: restoredLineItemId } : {}),
                };
              }
            });
            return Object.values(map);
          });
        }}
      />
      {noChargePrompt && (
        <NoChargeConfirmationModal
          lines={noChargePrompt.lines}
          vendorName={resolveSelectedVendorName({
            variant: noChargePrompt.isEdit ? "edit" : "create",
            selectedVendor,
            vendorsOptions,
            purchaseOrderDetails,
          })}
          mode={noChargePrompt.isEdit ? "edit" : "create"}
          isSubmitting={POIsCreateLoading() || POIsUpdateLoading()}
          onCancel={() => setNoChargePrompt(null)}
          onConfirm={confirmNoCharge}
        />
      )}
    </div>
  );
};

export default PurchaseOrderSidebar;
